import { useField } from 'formik'
import { useIntl } from 'react-intl'

import type { ContractDtoBidUnitEnum } from '@b-stock/contract-api-client'
import { BidInput } from '@b-stock/fe-core/bstock-next/components'
import {
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type { BidBasis, BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
  InputBordered,
  usePrecisionNumberFormat,
} from '@b-stock/fe-core/bstock-react'

import type { BidFormFormikValues } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { useProblemFreeOrderRateAnalytics } from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { ActionCardInput } from '../../ui'

export const AuctionBidInput = ({
  analyticsData,
  bidBasis,
  bidPrecision,
  bidUnit,
  currency = 'USD',
  disabled,
  isContract = false,
  isMax = false,
  minBidAmount = 0,
  shortUnit = false,
}: {
  analyticsData?: {
    listing: OrchestratedListingDetails
    screenName: string
  }
  bidBasis?: BidBasis
  bidPrecision?: BidPrecision
  bidUnit?: ContractDtoBidUnitEnum
  currency?: string
  disabled?: boolean
  isContract?: boolean
  isMax?: boolean
  minBidAmount?: number
  // Shared with the legacy bid box, which keeps the long-form unit.
  shortUnit?: boolean
}) => {
  const { formatMessage } = useIntl()
  const analytics = useAnalytics()
  const { trackButtonClicked } = analytics
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const isTlcDisplayed =
    !!analyticsData?.listing.lot &&
    (!!analyticsData.listing.lot.retailValue ||
      (getNumberOfUnitsInLot(analyticsData.listing.lot) ?? 0) > 0)
  const p2sData = useListingP2SData(
    analyticsData?.listing.lot.locationOriginalId
  )
  const problemFreeOrderRateAnalytics = useProblemFreeOrderRateAnalytics(
    analyticsData?.listing
  )

  const [{ value }, { error }, { setValue }] =
    useField<BidFormFormikValues['amount']>('amount')

  const showError = isMax ? false : !!error

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    precision: bidPrecision,
    precisionStored: 1,
    format: bidBasis === 'RELATIVE' ? 'percent' : 'currency',
    currency,
  })

  const perUnit = bidUnit
    ? formatMessage({
        id: shortUnit
          ? `Contracts.Enums.perUnit.shortSentenceCase.${bidUnit}`
          : `Contracts.Enums.perUnit.${bidUnit}`,
      })
    : ''

  const label = isMax ? (
    <FormattedMessage
      id="Auction.details.bidActions.bidInput.maxLabel"
      values={{ perUnit }}
    />
  ) : (
    <FormattedMessage
      id="Auction.details.bidActions.bidInput.label"
      values={{ min: formatPrecisionNumber(minBidAmount), perUnit }}
    />
  )

  const onFocus = () => {
    if (analyticsData) {
      trackButtonClicked({
        buttonName: 'bid_amt_field',
        screenName: analyticsData.screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        is_new_bid_box_design: true,
        is_total_landed_cost_displayed: isTlcDisplayed,
        ...getListingAnalyticsData(
          analyticsData.listing,
          fmvBadgeFlag,
          p2sData
        ),
        ...problemFreeOrderRateAnalytics,
      })
    }
  }

  return (
    <ActionCardInput>
      <BidInput
        allowFractionalCents={isContract}
        bidBasis={bidBasis}
        bidPrecision={bidPrecision}
        currencyCode={currency}
        disabled={disabled || isMax}
        error={showError}
        handleValueChange={(x) => setValue(x ?? 0)}
        label={label}
        name="amount"
        pricingStrategy="AUCTION"
        onFocus={onFocus}
        /**
         * `|| null` causes 0 value to be rendered as empty.
         * This sleight of hand treats zero as if it were null.
         * Workaround is needed because formik context value is not nullable.
         *
         * If we did not do this, the value would start out as `0` and the user
         * would have to clear the 0 before they type. This sucks for `0.00`.
         *
         * This causes a subtle issue where 0, on blur, sticks intermittently.
         * Enter 1, blur, enter 0, blur, and it disappears.
         * Enter 0 again, blur, and it stays.
         * The fix is to make the stored value nullable.
         * Then empty would be empty (null) and zero would be zero.
         */
        storedValue={value || null}
      />
      {showError && (
        <InputBordered.ErrorMessage>{error}</InputBordered.ErrorMessage>
      )}
    </ActionCardInput>
  )
}
